import { withPageLoading } from './page-loading'

export function apiUrl(path: string) {
  if (/^https?:\/\//i.test(path)) return path
  return path.startsWith('/') ? path : `/${path}`
}

export function apiAssetUrl(path: string | null | undefined) {
  if (!path) return null
  return apiUrl(path)
}

export async function apiFetch(path: string, init: RequestInit = {}) {
  return withPageLoading(() => request(path, init))
}

async function request(path: string, init: RequestInit, csrfRetried = false): Promise<Response> {
  const url = apiUrl(path)
  const sameOrigin = typeof window !== 'undefined' && new URL(url, window.location.origin).origin === window.location.origin
  const headers = new Headers(init.headers)
  if (sameOrigin && !['GET', 'HEAD', 'OPTIONS'].includes((init.method || 'GET').toUpperCase())) {
    let token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
    if (!token) {
      const csrf = await fetch('/api/auth/csrf', { credentials: 'same-origin', cache: 'no-store' })
      if (!csrf.ok) throw new Error('Die Sicherheitsprüfung ist fehlgeschlagen. Bitte lade die Seite neu.')
      token = (await csrf.json() as { csrfToken: string }).csrfToken
      storeCsrfToken(token)
    }
    headers.set('X-CSRF-Token', token)
  }
  const response = await fetch(url, {
    credentials: 'include',
    ...init,
    headers,
  })
  const nextToken = sameOrigin ? response.headers.get('X-CSRF-Token') : null
  if (nextToken) storeCsrfToken(nextToken)
  // Rails rejects invalid tokens before the action runs, so this exact failure
  // can safely be retried once after refreshing the token for the current session.
  if (sameOrigin && headers.has('X-CSRF-Token') && response.status === 422 && !csrfRetried && !(init.body instanceof ReadableStream)) {
    const data = await response.clone().json().catch(() => null) as { error?: string } | null
    if (data?.error === 'csrf_invalid') {
      const csrf = await fetch('/api/auth/csrf', { credentials: 'same-origin', cache: 'no-store' })
      if (!csrf.ok) throw new Error('Die Sicherheitsprüfung ist fehlgeschlagen. Bitte lade die Seite neu.')
      storeCsrfToken((await csrf.json() as { csrfToken: string }).csrfToken)
      return request(path, init, true)
    }
  }
  if (sameOrigin && path.startsWith('/api/admin/') && response.status === 403) {
    const data = await response.clone().json().catch(() => null) as { error?: string; redirectTo?: string } | null
    if (data?.error === 'admin_2fa_required' && data.redirectTo?.startsWith('/admin/security?')) {
      window.location.assign(data.redirectTo)
    }
  }
  return response
}

function storeCsrfToken(token: string) {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'csrf-token'
    document.head.appendChild(meta)
  }
  meta.content = token
}

export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await apiFetch(path, {
    ...init,
    headers,
  })

  if (!response.ok) {
    const data = await response.json().catch(() => null) as { error?: string; message?: string } | null
    throw new Error(data?.message || data?.error || `Request failed with ${response.status}`)
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}
